/**
 * useCurrentStageValue — hook that looks up the current pipeline stage
 * for a CRM entity by reading its associations from the CRM store.
 *
 * Pipeline stages are stored as associations (e.g. ahoy_deal_to_deal_pipeline_stage)
 * not as properties, so useCrmEntityProps won't include them. This hook
 * subscribes to the CRM store object and extracts the stage association's
 * target entity_id, giving the "current stage" for display in diff views.
 *
 * Returns a map of stage property keys → entity_ids.
 */

import { useMemo } from 'react';

import { useCrmObjectLive } from '@/lib/crm/hooks';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { ENTITY_TYPE_TO_STAGE_ASSOC } from '@/lib/proposals/stageAssociationTypes';

/**
 * Look up the current stage entity_id for an entity from the CRM store.
 *
 * Reads the LIVE record: this feeds the "current" column of a diff, and the
 * projected record inside a preview provider already carries the proposed
 * stage — reading that would make current and proposed identical.
 *
 * Uses dynamic association type data from the schema API to determine which
 * association represents a stage. Falls back to hardcoded mapping when schema
 * data is not yet loaded.
 *
 * @param orgId - Org ID for triggering fetch
 * @param entityId - The CRM entity whose stage we want
 * @param stagePropertyKeys - The stage property keys from the proposal (matched against associations)
 * @param entityTypeName - The entity type (e.g. "ahoy_deal") to determine which association to look for
 * @returns Map of stage property key → current stage entity_id
 */
export function useCurrentStageValue(
  orgId: number | undefined,
  entityId: string | undefined,
  stagePropertyKeys: string[],
  entityTypeName: string
): Record<string, string> {
  const storeObject = useCrmObjectLive(entityId, orgId);
  const { stageAssocForEntityType } = useAssociationTypes(orgId);

  return useMemo(() => {
    const result: Record<string, string> = {};
    if (stagePropertyKeys.length === 0 || !storeObject) {
      return result;
    }

    // Use dynamic mapping when available, fall back to hardcoded map
    const assocType =
      stageAssocForEntityType.get(entityTypeName) ??
      ENTITY_TYPE_TO_STAGE_ASSOC[entityTypeName];
    if (!assocType || !storeObject?.associations) {
      return result;
    }

    const stageAssoc = storeObject.associations.find(
      a => a.association_type_name === assocType
    );
    if (!stageAssoc) {
      return result;
    }

    // Map each stage property key to the current stage entity_id
    for (const key of stagePropertyKeys) {
      result[key] = stageAssoc.target_entity_id;
    }

    return result;
  }, [stagePropertyKeys, storeObject, entityTypeName, stageAssocForEntityType]);
}
